import React, { FunctionComponent, useCallback } from 'react';

import { useParams } from 'react-router';
import { connect, useDispatch } from 'react-redux';

import { Translate } from 'react-localize-redux';

import moment from 'moment';

import DineroFactory, { DineroObject } from 'dinero.js';

import styled from 'styled-components';

import AppState from '@webapp/store/state';
import {
  GenerateExportUrl,
  ListEarnings,
} from '@webapp/store/earnings/action-creators';

import { EarningsContainer, formatNumber } from '..';
import DetailBoxItem, { EarningsBoxDetailItem } from './detail-box-item';
import { EarningsResponse } from '@services/earnings/types/earnings-response';

import SummaryDetailsCtrl from '@webapp/controllers/summary';
import { StreamingLogoDecorator } from '@webapp/components/basics/streaming-logos/streaming-logo-decorator';
import { DetailItemProps } from '@webapp/pages/earnings/details/detail-item';
import { push } from 'connected-react-router';

interface MerchItemDetails {
  id: string;
  orders: number;
  products: number;
  earned: DineroFactory.Dinero;
  items: {
    label: string;
    earned: DineroFactory.Dinero;
    sales: number;
  }[];
}

let merchItemsTemplate = {
  id: 'us',
  title: 'United States',
  totalEarnings: DineroFactory({ amount: 0, currency: 'USD', precision: 5 }),
  merchItemDetails: [
    {
      id: 'apparel',
      title: (
        <Translate
          id="EARNINGS.DETAILS.MERCH.ITEM.APPAREL"
          data={{ amount: formatNumber(0) }}
        />
      ),
      earned: DineroFactory({ amount: 0, currency: 'USD', precision: 5 }),
      items: [],
    },
  ],
};

interface EarningsDetailsPageProps {
  className?: string;
  artistEarnings: EarningsResponse[];
  artistId: string;
  isExampleData: boolean;
}

const mapStateToProps = (state: AppState) => ({
  artistEarnings: state.earnings.data.ARTIST || [],
  artistId: state.artist.active?.id!,
  isExampleData:
    (state.royalties.artists[state.artist.active?.id || '']?.statements || [])
      .length === 0,
});

const EarningsDetailsPage: FunctionComponent<EarningsDetailsPageProps> = ({
  className,
  artistEarnings,
  artistId,
  isExampleData,
}) => {
  const { section, reportDate } = useParams<{
    section: string;
    reportDate: string;
  }>();

  const dispatch = useDispatch();

  const [selectedDate, setSelectedDate] = React.useState<string>(reportDate);
  const [hasDateChange, setHasDateChange] = React.useState(false);

  const [formattedMerchItems, setFormattedMerchItems] = React.useState<
    EarningsBoxDetailItem[]
  >([]);
  const [formattedReleaseItems, setFormattedReleaseItems] = React.useState<
    EarningsBoxDetailItem[]
  >([]);

  React.useEffect(() => {
    dispatch(
      push(`/earnings/${section}/${moment(selectedDate).format('YYYY-MM')}`)
    );
  }, [dispatch, section, selectedDate]);

  const handleChange = (date: string) => {
    setHasDateChange(!hasDateChange);
    setSelectedDate(date);
  };

  React.useEffect(() => {
    if (artistId && !isExampleData) {
      if (artistEarnings.find((val) => val.reportDate === reportDate)) {
        return;
      }
      dispatch(new ListEarnings('ARTIST', artistId as string, '', reportDate));
    }
  }, [dispatch, artistId, section, reportDate, artistEarnings, isExampleData]);

  const getSectionName = (section: string) => {
    switch (section) {
      case 'MUSIC':
        return <Translate id="EARNINGS.LIST.MUSIC.TITLE" />;
      case 'MERCH':
        return <Translate id="EARNINGS.LIST.MERCH.TITLE" />;
      case 'NFT':
        return <Translate id="EARNINGS.LIST.NFT.TITLE" />;
      default:
        break;
    }
  };

  const formatCountry = (postalAbbr: string = '') => {
    let country;
    switch (postalAbbr) {
      case 'US':
        country = 'United States';
        break;

      default:
        country = postalAbbr;
        break;
    }
    return country;
  };

  const formatMerchItems = useCallback(() => {
    let EarningsItemsByCountry: EarningsBoxDetailItem[] = [];

    const merchEarnings = artistEarnings.filter((val) => val.type === 'Merch');

    const countryMerchEarnings = merchEarnings.reduce(
      (acc: Record<string, any>, merchEarning) => {
        const merchOrderIds =
          merchEarning?.earningsByMerch &&
          Object.keys(merchEarning.earningsByMerch);
        if (merchOrderIds?.length) {
          merchOrderIds.forEach((merchOrderId) => {
            const merchEarningProduct =
              merchEarning.earningsByMerch?.[merchOrderId];

            const country = merchEarningProduct?.country;

            if (merchEarningProduct && country) {
              if (!acc[country]) {
                acc[country] = [];
              }

              acc[country].push({
                merchOrderId,
                label: merchEarningProduct.productTitle,
                total: DineroFactory({
                  amount:
                    (merchEarningProduct?.total as unknown as DineroObject)
                      ?.amount ?? 0,
                  currency:
                    (merchEarningProduct?.total as unknown as DineroObject)
                      ?.currency ?? 'USD',
                  precision:
                    (merchEarningProduct?.total as unknown as DineroObject)
                      ?.precision ?? 5,
                }),

                quantity: merchEarningProduct.quantity,
                productId: merchEarningProduct.productId,
                reportDate: `${merchEarning.reportDate}-15`,
              });
            }
          });
        }
        return acc;
      },
      {}
    );

    Object.keys(countryMerchEarnings).forEach((countryId) => {
      let earningsByCountry: EarningsBoxDetailItem = {
        ...merchItemsTemplate,
        id: countryId || '',
        title: formatCountry(countryId),
        details: [],
        totalEarnings: countryMerchEarnings[countryId].reduce(
          (acc, merchEarningItem) => {
            acc = acc.add(merchEarningItem.total);
            return acc;
          },
          DineroFactory({
            amount: 0,
            currency: 'USD',
            precision: 5,
          })
        ),
      };

      const merchItemDetails: Record<string, MerchItemDetails> =
        countryMerchEarnings[countryId]?.reduce(
          (acc: Record<string, MerchItemDetails>, merchEarningItem) => {
            const merchProductId = merchEarningItem.productId;
            if (merchProductId) {
              if (!acc[merchProductId]) {
                acc[merchProductId] = {
                  id: merchProductId,
                  orders: 0,
                  products: 0,
                  earned: DineroFactory({
                    amount: 0,
                    currency: 'USD',
                    precision: 5,
                  }),
                  items: [],
                };
              }

              acc[merchProductId].orders += 1;
              acc[merchProductId].products += merchEarningItem.quantity;
              acc[merchProductId].earned = acc[merchProductId].earned.add(
                merchEarningItem.total
              );
              acc[merchProductId].items = [
                {
                  label: merchEarningItem.label,
                  earned: acc[merchProductId].earned,
                  sales: acc[merchProductId].products,
                },
              ];
            }
            return acc;
          },
          {}
        ) ?? {};

      if (Object.values(merchItemDetails).length) {
        earningsByCountry.details = Object.values(merchItemDetails).reduce(
          (acc: DetailItemProps[], detailItem: MerchItemDetails) => {
            acc.push({
              key: detailItem.id,
              id: detailItem.id,
              title: (
                <Translate
                  id="EARNINGS.DETAILS.MERCH.ITEM.APPAREL"
                  data={{ amount: formatNumber(detailItem.orders) }}
                />
              ),
              earned: detailItem.earned,
              items: [...detailItem.items],
            } as DetailItemProps);

            return acc;
          },
          []
        ) as DetailItemProps[];
      }

      EarningsItemsByCountry.push(earningsByCountry);
    });
    setFormattedMerchItems(EarningsItemsByCountry);
  }, [artistEarnings]);

  const getTemplate = (): EarningsBoxDetailItem => {
    return {
      id: '',
      title: '',
      totalEarnings: DineroFactory({
        amount: 0,
        currency: 'USD',
        precision: 5,
      }),
      details: [
        {
          id: '',
          title: (
            <Translate
              id="EARNINGS.DETAILS.RECORDED_MUSIC.ITEM.AUDIO_STREAMS"
              data={{ amount: formatNumber(0) }}
            />
          ),
          titleHelper: (
            <Translate id="EARNINGS.DETAILS.RECORDED_MUSIC.ITEM.AUDIO_STREAMS.HELPER" />
          ),
          earned: DineroFactory({ amount: 0, currency: 'USD', precision: 5 }),
          subtitle: (
            <Translate
              id="EARNINGS.DETAILS.RECORDED_MUSIC.ITEM.AUDIO_STREAMS.SUBTITLE"
              data={{ amount: formatNumber(43) }}
            />
          ),
          items: [],
        },
      ],
    };
  };

  const formatReleaseItems = () => {
    const streamingEarnings = artistEarnings.filter(
      (val) => val.type !== 'Merch'
    );
    const Items: EarningsBoxDetailItem[] = [];
    streamingEarnings.forEach((rel) => {
      if (rel.earningsByRelease) {
        Object.keys(rel.earningsByRelease || {}).forEach((key) => {
          rel.earningsByRelease![key].entries.forEach((val) => {
            const tempTemplate: EarningsBoxDetailItem =
              Items.find((i) => i.id === val.dsp) || getTemplate();
            if (rel.earningsByRelease) {
              tempTemplate.id = val.dsp;
              tempTemplate.title = val.dsp;
              tempTemplate.icon = StreamingLogoDecorator(val.dsp);
              tempTemplate.details[0].subtitle = (
                <Translate
                  id="EARNINGS.DETAILS.RECORDED_MUSIC.ITEM.AUDIO_STREAMS.SUBTITLE"
                  data={{
                    amount: formatNumber(
                      Object.keys(rel.earningsByRelease).length
                    ),
                  }}
                />
              );
              const amount = DineroFactory({
                amount: val.value['amount'],
                currency: val.value['currency'],
                precision: val.value['precision'],
              });
              tempTemplate.totalEarnings =
                tempTemplate.totalEarnings.add(amount);

              const track = tempTemplate.details[0]?.items!.find(
                (i) => i.label === val.trackTitle
              );

              if (track) {
                track.earned = track.earned.add(amount);
              } else {
                tempTemplate.details[0]?.items!.push({
                  label: val.trackTitle,
                  earned: amount,
                  subitems: [],
                });
              }
            }
            tempTemplate.details[0]!.items =
              tempTemplate.details[0]?.items!.sort((a, b) =>
                b.earned.subtract(a.earned).getAmount()
              );
            if (!Items.some((i) => i.id === val.dsp)) {
              Items.push(tempTemplate);
            }
          });
        });
      }
    });
    setFormattedReleaseItems(
      Items.sort((a, b) =>
        b.totalEarnings.subtract(a.totalEarnings).getAmount()
      )
    );
  };

  const handleDownload = () => {
    dispatch(
      new GenerateExportUrl(
        //update artist or owner type here
        'ARTIST',
        artistId,
        'monthly',
        moment(selectedDate).format('YYYY-MM'),
        'detail'
      )
    );
    return;
  };

  React.useEffect(() => {
    if (section === 'MERCH') {
      formatMerchItems();
    }
    if (section === 'MUSIC') {
      formatReleaseItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artistEarnings]);

  return (
    <EarningsContainer className={className || ''}>
      <SummaryDetailsCtrl
        className="summary-ctrl"
        sectionTitle={getSectionName(section)}
        selectedDate={selectedDate}
        hasDateChange={hasDateChange}
        handleDownload={handleDownload}
        handleDateChange={handleChange}
        hasData={
          formattedReleaseItems.some(
            (item) => !!item.totalEarnings?.getAmount()
          ) ||
          formattedMerchItems.some((item) => !!item.totalEarnings?.getAmount())
        }
      >
        {section === 'MUSIC' &&
          formattedReleaseItems.map((music, index) => (
            <DetailBoxItem
              key={music.id}
              id={`${music.id}-${index}`}
              title={music.title}
              icon={music.icon}
              totalEarnings={music.totalEarnings}
              details={music.details}
              isFirst={index === 0}
            />
          ))}
        {section === 'MERCH' &&
          formattedMerchItems.map((merch) => (
            <DetailBoxItem
              key={merch.id}
              id={merch.id}
              title={merch.title}
              totalEarnings={merch.totalEarnings}
              details={merch.details}
            />
          ))}
      </SummaryDetailsCtrl>
    </EarningsContainer>
  );
};

export default connect(mapStateToProps)(styled(EarningsDetailsPage)`
  .summary-ctrl {
    height: fit-content;
    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      grid-column: 4 / 22;
    }

    ${({ theme }) => `@media screen and ${theme.device.laptop}`} {
      grid-column: 8 / 18;
    }
  }
`);
